import React from 'react';
import { ThemeService } from '../services/themeService';

export type WebsiteSettings = {
  mode?: 'shop' | 'job' | 'med' | 'edu';
  categoriesEnabled?: Record<string, boolean>;
  subcategoriesEnabled?: Record<string, Record<string, boolean>>;
  [key: string]: any;
};

interface WebsiteSettingsContextValue {
  settings: WebsiteSettings | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

const WebsiteSettingsContext = React.createContext<WebsiteSettingsContextValue | undefined>(undefined);

export const WebsiteSettingsProvider: React.FC<{ children: React.ReactNode }>
= ({ children }) => {
  const [settings, setSettings] = React.useState<WebsiteSettings | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  const getSettingsBase = React.useCallback(() => {
    try {
      const port = window.location.port;
      const isDevDifferentOrigin = port && port !== '8000';
      if (isDevDifferentOrigin) {
        return `${window.location.protocol}//${window.location.hostname}:8000`;
      }
    } catch {
      // ignore
    }
    return '';
  }, []);

  const fetchSettings = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const base = getSettingsBase();
      // Prefer merged settings (public base + storage homepage overlay)
      let res = await fetch(`${base}/api/get-website-settings.php`, { cache: 'no-store' });
      if (!res.ok) {
        // Fallback: keep existing public settings behavior for now
        res = await fetch(`${base}/website-settings.json`, { cache: 'no-store' });
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSettings(data || {});
      // also expose globally for legacy consumers if needed
      (window as any).__websiteSettings = data || {};
      try {
        const currentTheme = ThemeService.getCurrentTheme();
        if (currentTheme === 'site-default') {
          ThemeService.applyTheme('site-default');
        }
      } catch {
        // ignore
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [getSettingsBase]);

  React.useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  React.useEffect(() => {
    const handler = (ev: any) => {
      const next = ev?.detail;
      if (!next || typeof next !== 'object') return;
      setSettings((prev) => ({ ...(prev || {}), ...(next || {}) }));
      try {
        (window as any).__websiteSettings = { ...((window as any).__websiteSettings || {}), ...(next || {}) };
      } catch {
        // ignore
      }
      try {
        const currentTheme = ThemeService.getCurrentTheme();
        if (currentTheme === 'site-default') {
          ThemeService.applyTheme('site-default');
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener('websiteSettingsUpdated', handler as any);
    return () => window.removeEventListener('websiteSettingsUpdated', handler as any);
  }, []);

  const value: WebsiteSettingsContextValue = React.useMemo(() => ({
    settings,
    loading,
    error,
    reload: fetchSettings,
  }), [settings, loading, error, fetchSettings]);

  return (
    <WebsiteSettingsContext.Provider value={value}>
      {children}
    </WebsiteSettingsContext.Provider>
  );
};

export const useWebsiteSettings = () => {
  const ctx = React.useContext(WebsiteSettingsContext);
  if (!ctx) throw new Error('useWebsiteSettings must be used within WebsiteSettingsProvider');
  return ctx;
};
